import { useRef, useState } from "react";
import { CheckCircle2, Headphones, Send } from "lucide-react";
import RecaptchaField from "../components/RecaptchaField";
import { api } from "../services/api";

const inputClasses =
  "w-full rounded-2xl border border-[#3991D5]/15 bg-white px-4 py-4 text-slate-900 placeholder:text-slate-400 transition focus:border-[#3991D5] focus:outline-none focus:ring-4 focus:ring-[#70C6FE]/20";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

function Support() {
  const [form, setForm] = useState(emptyForm);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const recaptchaRef = useRef(null);

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submitSupport = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setStatus({ type: "", message: "" });

    try {
      await api.post("/support/public/tickets", {
        ...form,
        recaptchaToken,
      });
      setForm(emptyForm);
      setRecaptchaToken("");
      recaptchaRef.current?.reset();
      setStatus({
        type: "success",
        message: "Your support request was sent. Our team will review it from the admin dashboard.",
      });
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-[#f8fbff] px-4 py-12 text-slate-900 sm:px-8">
      <div className="container">
        <div className="mx-auto max-w-5xl rounded-[32px] border border-[#3991D5]/10 bg-white p-6 shadow-[0_18px_60px_rgba(57,145,213,0.12)] lg:p-10">
          <div className="mb-8 flex items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#3991D5] text-white">
              <Headphones className="h-6 w-6" />
            </span>
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#3991D5]">Support</p>
              <h1 className="mt-2 font-gopher text-4xl font-black uppercase text-[#0f172a] sm:text-5xl">How can we help?</h1>
              <p className="mt-3 max-w-2xl text-sm font-semibold leading-6 text-slate-500">
                Send your parts, order, shipping, or account question. Registered and guest customers can both submit requests.
              </p>
            </div>
          </div>

          <form className="grid gap-5" onSubmit={submitSupport}>
            <div className="grid gap-5 md:grid-cols-2">
              <label>
                <span className="mb-2 block text-sm font-bold text-slate-700">Name</span>
                <input className={inputClasses} name="name" onChange={updateForm} placeholder="Your name" value={form.name} />
              </label>
              <label>
                <span className="mb-2 block text-sm font-bold text-slate-700">Email</span>
                <input className={inputClasses} name="email" onChange={updateForm} placeholder="you@example.com" required type="email" value={form.email} />
              </label>
            </div>

            <div className="grid gap-5 md:grid-cols-2">
              <label>
                <span className="mb-2 block text-sm font-bold text-slate-700">Phone</span>
                <input className={inputClasses} name="phone" onChange={updateForm} placeholder="Optional" type="tel" value={form.phone} />
              </label>
              <label>
                <span className="mb-2 block text-sm font-bold text-slate-700">Subject</span>
                <input className={inputClasses} name="subject" onChange={updateForm} placeholder="Order help, part question, shipping..." value={form.subject} />
              </label>
            </div>

            <label>
              <span className="mb-2 block text-sm font-bold text-slate-700">What help do you need?</span>
              <textarea className={`${inputClasses} min-h-[170px] resize-none`} name="message" onChange={updateForm} placeholder="Describe the issue, part number, order number, machine model, or anything we should know." required value={form.message} />
            </label>

            <RecaptchaField ref={recaptchaRef} onVerify={setRecaptchaToken} />

            {status.message && (
              <div className={`rounded-2xl p-4 text-sm font-bold ${status.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                {status.message}
              </div>
            )}

            <button className="inline-flex w-fit items-center gap-3 rounded-full bg-[#3991D5] px-7 py-4 font-gopher text-sm font-bold uppercase tracking-wide text-white transition hover:bg-[#1a6db5] disabled:opacity-60" disabled={submitting} type="submit">
              {submitting ? "Sending..." : "Submit request"}
              {status.type === "success" ? <CheckCircle2 className="h-4 w-4" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default Support;
